import { useState } from "react";
import { Loader2, CheckCircle2, XCircle, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AiToolExecution } from "../hooks/useAiChat";
import { useI18n } from "../i18n";
import type { TranslationKeys } from "../i18n";

const TOOL_LABEL_KEYS: Record<string, keyof TranslationKeys> = {
  get_session_info: 'aiTool.getSessionInfo',
  get_current_page: 'aiTool.getCurrentPage',
  list_character_sheets: 'aiTool.listSheets',
  get_character_sheet: 'aiTool.getSheet',
  create_note: 'aiTool.createNote',
  search_srd_monsters: 'aiTool.searchMonsters',
  get_srd_monster: 'aiTool.getMonster',
  search_srd_classes: 'aiTool.searchClasses',
  search_srd_spells: 'aiTool.searchSpells',
  create_character_sheet: 'aiTool.createSheet',
  update_character_sheet: 'aiTool.updateSheet',
  create_token: 'aiTool.createToken',
  bind_sheet_to_token: 'aiTool.bindSheet',
  create_npc_with_sheet: 'aiTool.createNpc',
  initiative_add: 'aiTool.initiativeUpdate',
  initiative_remove: 'aiTool.initiativeUpdate',
  initiative_set_value: 'aiTool.initiativeUpdate',
  initiative_next: 'aiTool.initiativeNext',
  initiative_end: 'aiTool.initiativeReset',
  apply_damage: 'aiTool.applyDamage',
  apply_healing: 'aiTool.applyHealing',
  spell_slot_spend: 'aiTool.spellSlotSpend',
  concentration_set: 'aiTool.concentrationSet',
  effect_add: 'aiTool.effectAdd',
  effect_update: 'aiTool.effectUpdate',
  effect_remove: 'aiTool.effectRemove',
  repeating_row_add: 'aiTool.repeatingRowAdd',
  repeating_row_update: 'aiTool.repeatingRowUpdate',
  repeating_row_remove: 'aiTool.repeatingRowRemove',
  page_activate: 'aiTool.pageActivate',
  page_transfer_tokens: 'aiTool.pageTransfer',
  page_create: 'aiTool.pageCreate',
  page_rename: 'aiTool.pageRename',
  location_link_create: 'aiTool.locationLinkCreate',
  fog_reveal: 'aiTool.fogReveal',
  fog_reset: 'aiTool.fogReset',
  fog_toggle: 'aiTool.fogToggle',
  sheet_rest: 'aiTool.sheetRest',
  ability_add: 'aiTool.abilityAdd',
  ability_remove: 'aiTool.abilityRemove',
  spellbook_add: 'aiTool.spellbookAdd',
  spellbook_remove: 'aiTool.spellbookRemove',
  token_move: 'aiTool.tokenMove',
  token_update: 'aiTool.tokenUpdate',
  token_delete: 'aiTool.tokenDelete',
  chat_message: 'aiTool.chatMessage',
  chat_roll: 'aiTool.chatRoll',
  ruler_show: 'aiTool.rulerShow',
  search_journal: 'aiTool.searchJournal',
  get_journal_entry: 'aiTool.getJournalEntry',
  get_related_entries: 'aiTool.getRelatedEntries',
  create_journal_entry: 'aiTool.createJournalEntry',
  update_journal_entry: 'aiTool.updateJournalEntry',
  reveal_journal_entry: 'aiTool.revealJournalEntry',
  journal_link: 'aiTool.journalLink',
  get_srd_spell: 'aiTool.getSpell',
  search_srd_equipment: 'aiTool.searchEquipment',
  search_srd_magic_items: 'aiTool.searchMagicItems',
  search_srd_races: 'aiTool.searchRaces',
  search_srd_conditions: 'aiTool.searchConditions',
  create_timeline_events: 'aiTool.createTimelineEvents',
  generate_calendar: 'aiTool.generateCalendar',
};

export function formatToolArgs(args: Record<string, unknown>): string {
  return Object.entries(args)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(', ');
}

interface AiToolProgressProps {
  toolData: AiToolExecution;
  isGM?: boolean;
}

export default function AiToolProgress({ toolData, isGM }: AiToolProgressProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const labelKey = TOOL_LABEL_KEYS[toolData.toolName];
  const label = labelKey ? t(labelKey) : toolData.toolName;
  const argsStr = formatToolArgs(toolData.args);

  const StatusIcon = toolData.status === 'running' ? Loader2
    : toolData.status === 'done' ? CheckCircle2
    : XCircle;

  const statusClass = `ai-tool-progress--${toolData.status}`;
  const hasDetails = !!(argsStr || toolData.result);

  return (
    <div
      className={cn('ai-tool-progress', statusClass, hasDetails && 'cursor-pointer')}
      onClick={() => hasDetails && setExpanded(prev => !prev)}
    >
      <div className="ai-tool-progress__header">
        <StatusIcon
          size={14}
          data-testid={`tool-icon-${toolData.status}`}
          className={cn(
            toolData.status === 'running' && 'animate-spin',
            toolData.status === 'done' && 'text-ok',
            toolData.status === 'error' && 'text-danger',
          )}
        />
        <span className="ai-tool-progress__label">{label}</span>
        {hasDetails && (
          expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />
        )}
      </div>
      {toolData.status === 'error' && !expanded && (
        <div className="ai-tool-progress__error-hint text-warn text-meta">
          {t('ai.toolErrorFriendly')}
        </div>
      )}
      {expanded && argsStr && (
        <div className="ai-tool-progress__args">{argsStr}</div>
      )}
      {expanded && toolData.result && (
        <div className="ai-tool-progress__result text-meta text-dim">{toolData.result}</div>
      )}
    </div>
  );
}
